import { useEffect, useState } from "react";
import { Linking, Text, View } from "react-native";
import { CalendarDays, FileText } from "lucide-react-native";
import {
  ActionBanner,
  Dialog,
  DialogActions,
  DialogBodyText,
  DialogHeader,
  DialogIcon,
  PrimaryButton,
  SecondaryButton,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { getApiErrorMessage } from "@src/packages/api/api-error";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import type {
  LabResult,
  LabResultHistoryPoint,
} from "../screens/care/sections/ResultsLabsCard";
import {
  DocumentRow,
  DocumentTestResultRow,
} from "../screens/documents/DocumentRow";
import { getCycleDocumentTag } from "../screens/care/sections/CycleRelatedDocumentsSection";
import { useDocumentUploadController } from "../DocumentUploadController";
import { LabValueField } from "@src/packages/test-results/components/LabValueField";
import type { MetricCycleLink } from "../data/metricFactsheets";

const DELETE_CONFIRMATION_ILLUSTRATION =
  require("../../assets/images/you-icons/trash-bin.png") as number;

type ResultDialogMode = "view" | "edit" | "delete";

type Props = {
  result: LabResult | null;
  point: LabResultHistoryPoint | null;
  onClose: () => void;
  onEdit?: (
    id: string,
    data: { patientValue?: string; unit?: string; date?: string },
  ) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onAttachSource?: (id: string, attachmentId: string) => Promise<void>;
  cycleLink?: MetricCycleLink;
  onOpenCycle?: (cycleLink: MetricCycleLink) => void;
  testId?: string | null;
  onOpenTest?: (testId: string) => void;
  readOnly?: boolean;
};

function ResultSourceRow({
  point,
  cycleId,
  onAttachSource,
}: {
  point: LabResultHistoryPoint;
  cycleId?: string | null;
  onAttachSource?: (id: string, attachmentId: string) => Promise<void>;
}) {
  const t = useTranslations();
  const { openDocumentUpload } = useDocumentUploadController();
  const sourceUpload = point.attachment?.upload;
  const sourceUrl = sourceUpload?.url ?? null;
  const sourceAvailable = sourceUpload !== undefined && sourceUpload !== null;
  const sourceReady = sourceUrl !== null;

  const openUpload = () => {
    openDocumentUpload({
      defaultCategory: "medical_records",
      defaultTags: [
        "doctype:lab",
        ...(cycleId ? [getCycleDocumentTag(cycleId)] : []),
      ],
      onSave: ({ uploadId }) => {
        if (!uploadId || !onAttachSource) return;
        void onAttachSource(point.id, uploadId);
      },
    });
  };

  return (
    <View style={{ gap: 8 }}>
      <Text
        className="font-dm-sans"
        style={{
          color: EMBER.colors.textLight,
          fontSize: 12,
          letterSpacing: 0.4,
          textTransform: "uppercase",
        }}
      >
        {t("care.lab_result.source_document")}
      </Text>
      <DocumentRow
        title={sourceUpload?.name ?? t("care.lab_result.source_document_add")}
        subtitle={
          sourceAvailable
            ? sourceReady
              ? t("care.lab_result.source_document")
              : t("care.lab_result.source_document_pending")
            : t("care.lab_result.source_document_missing")
        }
        icon={<FileText size={18} color={EMBER.colors.textLight} />}
        status={
          sourceAvailable ? (sourceReady ? "on-file" : "requested") : "missing"
        }
        onPress={
          sourceReady ? () => void Linking.openURL(sourceUrl) : openUpload
        }
      />
    </View>
  );
}

function ResultCycleRow({
  cycleLink,
  onPress,
}: {
  cycleLink: MetricCycleLink;
  onPress?: () => void;
}) {
  const t = useTranslations();

  return (
    <DocumentRow
      title={cycleLink.label}
      subtitle={t("care.cycle_tests.linked_to_cycle")}
      icon={<CalendarDays size={18} color={EMBER.colors.textLight} />}
      onPress={onPress}
      showChevron={Boolean(onPress)}
    />
  );
}

export default function TestResultDetailDialog({
  result,
  point,
  onClose,
  onEdit,
  onDelete,
  onAttachSource,
  cycleLink,
  onOpenCycle,
  testId,
  onOpenTest,
  readOnly = false,
}: Props) {
  const t = useTranslations();
  const [mode, setMode] = useState<ResultDialogMode>("view");
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("");
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setMode("view");
    setPending(false);
    setErrorMessage(null);
    setValue(point?.value ?? "");
    setUnit(result?.unit ?? "");
  }, [point?.id, point?.value, result?.id, result?.unit]);

  const visible = result !== null && point !== null;
  const handleClose = () => {
    if (mode !== "view") {
      setMode("view");
      setErrorMessage(null);
      return;
    }
    onClose();
  };

  const handleEdit = async () => {
    if (!onEdit || !point) return;
    setPending(true);
    setErrorMessage(null);
    try {
      await onEdit(point.id, {
        patientValue: value || undefined,
        unit: unit || undefined,
      });
      onClose();
    } catch (error) {
      setErrorMessage(
        getApiErrorMessage(error, t("care.lab_result.edit_error")),
      );
    } finally {
      setPending(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || !point) return;
    setPending(true);
    setErrorMessage(null);
    try {
      await onDelete(point.id);
      onClose();
    } catch (error) {
      setErrorMessage(
        getApiErrorMessage(error, t("care.lab_result.delete_error")),
      );
    } finally {
      setPending(false);
    }
  };

  const handleOpenCycle = () => {
    if (!cycleLink || !onOpenCycle) return;
    onClose();
    onOpenCycle(cycleLink);
  };

  const handleOpenTest = () => {
    if (!testId || !onOpenTest) return;
    onClose();
    onOpenTest(testId);
  };

  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      closeButton="minimal"
      testID="test-result-detail-dialog"
      contentStyle={{
        paddingTop: 24,
        paddingHorizontal: 22,
        paddingBottom: 20,
      }}
    >
      {result && point && mode === "view" ? (
        <>
          <DialogHeader
            title={`${result.name} result`}
            subtitle={point.date}
            style={{ marginBottom: 18 }}
          />

          <View style={{ gap: 18 }}>
            <DocumentTestResultRow
              title={result.name}
              subtitle={result.subtitle ?? result.description}
              dateLabel={point.date}
              value={point.value}
              unit={result.unit}
              // The row she tapped resolved this point's category with the
              // shared rule, so the dialog shows the same position.
              category={point.category}
            />

            {testId && onOpenTest ? (
              <DocumentRow
                title={t("care.lab_result.view_test_details")}
                subtitle={result.name}
                icon={<FileText size={18} color={EMBER.colors.textLight} />}
                onPress={handleOpenTest}
                showChevron
              />
            ) : null}

            {point.clinicalAssessmentDetails ||
            result.clinicalAssessmentDetails ? (
              <View style={{ gap: 5 }}>
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 12, color: EMBER.colors.textLight }}
                >
                  {t("care.lab_result.context_title")}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    color: EMBER.colors.textMid,
                    lineHeight: 20,
                  }}
                >
                  {point.clinicalAssessmentDetails ??
                    result.clinicalAssessmentDetails}
                </Text>
              </View>
            ) : null}

            {cycleLink ? (
              <ResultCycleRow
                cycleLink={cycleLink}
                onPress={onOpenCycle ? handleOpenCycle : undefined}
              />
            ) : null}

            <ResultSourceRow
              point={point}
              cycleId={cycleLink?.cycleId ?? result.cycleId}
              onAttachSource={onAttachSource}
            />
          </View>

          {!readOnly && (onEdit || onDelete) ? (
            <DialogActions style={{ marginTop: 20 }}>
              {onDelete ? (
                <SecondaryButton
                  label={t("care.lab_result.delete")}
                  onPress={() => {
                    setErrorMessage(null);
                    setMode("delete");
                  }}
                  flex={1}
                />
              ) : null}
              {onEdit ? (
                <PrimaryButton
                  label={t("care.lab_result.edit_result_title")}
                  onPress={() => {
                    setErrorMessage(null);
                    setMode("edit");
                  }}
                  flex={1}
                />
              ) : null}
            </DialogActions>
          ) : null}
        </>
      ) : null}

      {result && point && mode === "edit" ? (
        <>
          <DialogHeader
            title={t("care.lab_result.edit_result_title")}
            subtitle={`${result.name} · ${point.date}`}
            style={{ marginBottom: 18 }}
          />
          <View style={{ gap: 14 }}>
            {/* The same field as the AMH value: the unit sits inside it. */}
            <LabValueField
              label={t("care.lab_result.edit_value_label")}
              value={value}
              onChangeValue={setValue}
              unit={unit}
              onChangeUnit={setUnit}
              marker={result.name}
              keyboardType="decimal-pad"
              placeholder={t("care.lab_result.edit_value_placeholder")}
            />
            {errorMessage ? (
              <ActionBanner variant="important" message={errorMessage} />
            ) : null}
            <DialogActions style={{ marginTop: 4 }}>
              <SecondaryButton
                label={t("care.lab_result.edit_cancel")}
                onPress={() => setMode("view")}
                disabled={pending}
                flex={1}
              />
              <PrimaryButton
                label={t("care.lab_result.edit_save")}
                onPress={() => void handleEdit()}
                loading={pending}
                disabled={pending}
                flex={1}
              />
            </DialogActions>
          </View>
        </>
      ) : null}

      {result && point && mode === "delete" ? (
        <>
          <DialogIcon source={DELETE_CONFIRMATION_ILLUSTRATION} />
          <DialogHeader
            align="center"
            title={t("care.lab_result.delete_value_prompt")}
            subtitle={`${result.name} · ${point.date}`}
            style={{ marginTop: 16, marginBottom: 6 }}
          />
          <DialogBodyText style={{ marginTop: 0, textAlign: "center" }}>
            {t("care.lab_result.delete_confirm")}
          </DialogBodyText>
          {errorMessage ? (
            <ActionBanner
              variant="important"
              message={errorMessage}
              style={{ marginTop: 6 }}
            />
          ) : null}
          <DialogActions style={{ marginTop: 20 }}>
            <SecondaryButton
              label={t("care.lab_result.cancel")}
              onPress={() => setMode("view")}
              disabled={pending}
              flex={1}
            />
            <SecondaryButton
              variant="destructive"
              label={t("care.lab_result.delete")}
              onPress={() => void handleDelete()}
              loading={pending}
              flex={1}
            />
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );
}
